import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, useTheme } from '@mui/material';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/CxEditorClient';

type PaletteToken = { id: string; label: string; hex: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (token: PaletteToken) => void;
}

export const PalettePickerDialog: React.FC<Props> = ({ open, onClose, onSelect }) => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const [tokens, setTokens] = React.useState<PaletteToken[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    let ignore = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const data = await api.getPalette();
        const groups = data?.groups || [];
        const list: PaletteToken[] = [];
        groups.forEach((g: any) => (g.tokens || []).forEach((t: any) => list.push({ id: t.id, label: t.label || t.id, hex: t.hex })));
        if (!ignore) setTokens(list);
      } catch (e: any) {
        if (!ignore) setError('Failed to load palette');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [open, rc]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Select from Palette</DialogTitle>
      <DialogContent>
        {loading && <Box sx={{ padding: 1 }}>Loading…</Box>}
        {error && <Box sx={{ color: 'error.main', padding: 1 }}>{error}</Box>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, marginTop: 1 }}>
          {tokens.map(tok => (
            <Box
              key={tok.id}
              component="button"
              onClick={() => { onSelect(tok); onClose(); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 0.5,
                  background: tok.hex,
                  border: 1,
                  borderColor: 'divider'
                }}
              />
              <Box component="span" sx={{ fontSize: 12 }}>{tok.label}</Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PalettePickerDialog;


